import { useAuth0 } from "@auth0/auth0-react";
import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { COMMON_COPY } from "copy/common";
import { useCopy } from "i18n/useCopy";

import {
  ACTIVE_ORG_ID_STORAGE_KEY,
  PERSONAL_WORKSPACE_SELECTION,
} from "../constants/organization/organizationWorkspace";
import { OrganizationService } from "../services/muggleTest/organizationService";
import { isAuthenticationError } from "../types/errors/ApiErrors";
import { IMyOrganizationEntry } from "../types/organization/IMyOrganizationEntry";
import { IOrganization } from "../types/organization/IOrganization";
import { IOrganizationContext } from "../types/organization/IOrganizationContext";
import { IOrganizationMember } from "../types/organization/IOrganizationMember";
import { OrganizationStatusEnum } from "../types/organization/OrganizationStatusEnum";
import { resolveActiveOrganizationId } from "../utils/organization/resolveActiveOrganizationId";

const OrganizationContext = createContext<IOrganizationContext | undefined>(undefined);

/**
 * Internal accessor used by `useOrganization`. Exported only so the
 * hook file can import it without depending on a barrel.
 */
export const InternalOrganizationContext = OrganizationContext;

interface OrganizationProviderProps {
  children: ReactNode;
}

/**
 * Read the persisted workspace selection from localStorage. Returns null
 * when nothing is persisted or localStorage is unavailable.
 */
function readPersistedSelection(): string | null {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return null;
  }
  try {
    return window.localStorage.getItem(ACTIVE_ORG_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Persist the workspace selection, or clear it when `selection` is null.
 * Silently swallows localStorage failures because they are non-fatal.
 */
function writePersistedSelection(selection: string | null): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return;
  }
  try {
    if (selection === null) {
      window.localStorage.removeItem(ACTIVE_ORG_ID_STORAGE_KEY);
    } else {
      window.localStorage.setItem(ACTIVE_ORG_ID_STORAGE_KEY, selection);
    }
  } catch {
    // best-effort persistence — non-fatal
  }
}

/**
 * Provides the user's organization memberships and the currently active
 * organization to consumer components.
 *
 * Memberships come from `/my-organizations`, and the active organization
 * is resolved from the persisted workspace selection against them: a new
 * browser lands on the user's organization, and a selection the user has
 * lost access to falls back instead of dead-ending.
 */
export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated, isLoading: isAuthLoading } = useAuth0();

  const [memberships, setMemberships] = useState<IMyOrganizationEntry[]>([]);
  const [isResolvingMemberships, setIsResolvingMemberships] = useState<boolean>(true);
  const [activeOrganizationId, setActiveOrganizationIdState] = useState<string | null>(null);
  const [organization, setOrganization] = useState<IOrganization | null>(null);
  const [members, setMembers] = useState<IOrganizationMember[]>([]);
  const [isFetchingOrganization, setIsFetchingOrganization] = useState<boolean>(false);
  const copy = useCopy();
  const [error, setError] = useState<string | null>(null);
  // A non-Error rejection carries no message; its fallback text resolves at render so it follows the language.
  const [isUnknownLoadError, setIsUnknownLoadError] = useState<boolean>(false);
  const [organizationRefreshCount, setOrganizationRefreshCount] = useState<number>(0);

  const redirectToLogin = useCallback(async (): Promise<void> => {
    await loginWithRedirect({ appState: { returnTo: window.location.pathname + window.location.search } });
  }, [loginWithRedirect]);

  /**
   * Load the user's memberships and resolve the active organization from
   * the persisted selection. A resolved organization is persisted; when
   * none resolves, a stale id is cleared but an explicit personal choice
   * is kept.
   */
  const loadMemberships = useCallback(async (): Promise<void> => {
    if (isAuthLoading || !isAuthenticated) {
      return;
    }
    setIsResolvingMemberships(true);
    try {
      const authToken = await getAccessTokenSilently();
      const fetchedMemberships = await OrganizationService.listMyOrganizations(authToken);
      const persistedSelection = readPersistedSelection();
      const resolvedOrganizationId = resolveActiveOrganizationId(persistedSelection, fetchedMemberships);
      if (resolvedOrganizationId !== null) {
        writePersistedSelection(resolvedOrganizationId);
      } else if (persistedSelection !== PERSONAL_WORKSPACE_SELECTION) {
        writePersistedSelection(null);
      }
      setMemberships(fetchedMemberships);
      setActiveOrganizationIdState(resolvedOrganizationId);
    } catch (err) {
      console.error("Failed to load organization memberships:", err);
      if (isAuthenticationError(err)) {
        await redirectToLogin();
        return;
      }
      setError(err instanceof Error ? err.message : "Unknown error loading organizations");
      setIsUnknownLoadError(false);
    } finally {
      setIsResolvingMemberships(false);
    }
  }, [getAccessTokenSilently, isAuthLoading, isAuthenticated, redirectToLogin]);

  /**
   * Fetch the active organization and its member list.
   *
   * When the active org is SUSPENDED or CANCELED we still fetch the org
   * itself (the backend allow-lists `GET /organizations/:orgId` for
   * suspended orgs) but skip the member list call, which would 403 with
   * "Organization is suspended". Consumers can branch on
   * `organization.status` to render a suspended/billing-prompt UI.
   *
   * @param isSuperseded - Reports whether a newer selection replaced this fetch; its results are then dropped.
   */
  const fetchActiveOrganization = useCallback(
    async (isSuperseded: () => boolean): Promise<void> => {
      if (!activeOrganizationId) {
        setOrganization(null);
        setMembers([]);
        setError(null);
        setIsUnknownLoadError(false);
        setIsFetchingOrganization(false);
        return;
      }

      setIsFetchingOrganization(true);
      setError(null);
      setIsUnknownLoadError(false);
      try {
        const authToken = await getAccessTokenSilently();
        const fetchedOrganization = await OrganizationService.getOrganization(activeOrganizationId, authToken);
        const fetchedMembers =
          fetchedOrganization.status === OrganizationStatusEnum.ACTIVE
            ? await OrganizationService.listMembers(activeOrganizationId, authToken)
            : [];
        if (isSuperseded()) {
          return;
        }
        setOrganization(fetchedOrganization);
        setMembers(fetchedMembers);
      } catch (err) {
        if (isSuperseded()) {
          return;
        }
        console.error("Failed to fetch active organization:", err);
        if (isAuthenticationError(err)) {
          await redirectToLogin();
          return;
        }
        setError(err instanceof Error ? err.message : null);
        setIsUnknownLoadError(!(err instanceof Error));
        setOrganization(null);
        setMembers([]);
      } finally {
        if (!isSuperseded()) {
          setIsFetchingOrganization(false);
        }
      }
    },
    [activeOrganizationId, getAccessTokenSilently, redirectToLogin]
  );

  useEffect(() => {
    void loadMemberships();
  }, [loadMemberships]);

  useEffect(() => {
    let isSuperseded = false;
    void fetchActiveOrganization(() => isSuperseded);
    return () => {
      isSuperseded = true;
    };
  }, [fetchActiveOrganization, organizationRefreshCount]);

  const setActiveOrganizationId = useCallback(
    (organizationId: string | null): void => {
      writePersistedSelection(organizationId ?? PERSONAL_WORKSPACE_SELECTION);
      const isLoadedMembership =
        organizationId === null || memberships.some((membership) => membership.organization.id === organizationId);
      if (isLoadedMembership) {
        setActiveOrganizationIdState(organizationId);
        return;
      }
      // A just-created organization is not in the loaded list yet; reloading resolves the selection just persisted.
      void loadMemberships();
    },
    [loadMemberships, memberships]
  );

  const refresh = useCallback(async (): Promise<void> => {
    await loadMemberships();
    setOrganizationRefreshCount((count) => count + 1);
  }, [loadMemberships]);

  const currentUserRole = useMemo(
    () => memberships.find((membership) => membership.organization.id === activeOrganizationId)?.role ?? null,
    [memberships, activeOrganizationId]
  );
  const memberCount = members.length;
  const loading = isResolvingMemberships || isFetchingOrganization;

  const value = useMemo<IOrganizationContext>(
    () => ({
      organization: organization,
      activeOrganizationId: activeOrganizationId,
      isWorkspaceResolved: !isResolvingMemberships,
      memberships: memberships,
      currentUserRole: currentUserRole,
      memberCount: memberCount,
      loading: loading,
      error: isUnknownLoadError ? copy.text(COMMON_COPY.organizationContext.errors.loadFailedUnknown) : error,
      setActiveOrganizationId: setActiveOrganizationId,
      refresh: refresh,
    }),
    [
      organization,
      activeOrganizationId,
      isResolvingMemberships,
      memberships,
      currentUserRole,
      memberCount,
      loading,
      error,
      isUnknownLoadError,
      copy,
      setActiveOrganizationId,
      refresh,
    ]
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};
